import {GameRoundPresentation} from "@nolimitcity/slot-game/bin/timelinegameround/core/gameround/GameRoundPresentation";
import {GameTutorialView} from "./GameTutorialView";
import {TimelineLite} from "gsap";
import {BookOfShadowsGameModuleConfig} from "../../../BookOfShadowsGameModuleConfig";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {BookOfShadowsGameConfig} from "../../../BookOfShadowsGameConfig";
import {BookOfShadows} from "../../../BookOfShadows";
import Timeout = NodeJS.Timeout;

export class GameTutorialController implements GameRoundPresentation {
    private _counter: number = -1;
    private _view: GameTutorialView;
    private _hasFSFinished: boolean = false;
    private _actFinishTimeoutLocks: Timeout;
    private _actFinishTimeoutRows: Timeout;

    /**
     * @constructs
     */
    constructor() {
        this._view = new GameTutorialView();
    }

    private _spinStarted: boolean = false;

    set spinStarted(value: boolean) {
        this._spinStarted = value;
        value && this._actFinishTimeoutLocks && clearTimeout(this._actFinishTimeoutLocks);
        value && this._actFinishTimeoutRows && clearTimeout(this._actFinishTimeoutRows);
    }

    /**
     * @description When FS is over.
     */
    public exitFS(): void {
        this._counter = -1;
        this._hasFSFinished = true;
    }

    /**
     * @inheritDoc
     */
    public getAnimation(): TimelineLite | null {
        if (GameSetting.replayMode || BookOfShadows.api.freeBets.hasFreeBets()) {
            return null;
        }

        // if feature is allowed
        if (BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_JURISDICTION) {
            // #165
            //#211 timeout has been added and released when spin started.
            this._actFinishTimeoutLocks = setTimeout(() => {
                BookOfShadowsGameModuleConfig.reelLockController.actFinish();
            }, 200);
        }

        // if feature is allowed
        if (BookOfShadowsGameConfig.instance.ACTIVE_ROWS_ALLOWED_JURISDICTION) {
            // #165
            //#211 timeout has been added and released when spin started.
            this._actFinishTimeoutRows = setTimeout(() => {
                BookOfShadowsGameModuleConfig.inactiveRowController.onIdle();
            }, 200);
        }

        // #242
        // if feature is not allowed
        if (!BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_VIA_BET) {
            return null;
        }

        this._counter++;

        const startOffset: number = 1,
            duration: number = 4.8,
            defaultRows: number = 3,
            defaultLockSetUp: string = JSON.stringify(["0", "0", "0", "0", "0"]);

        if (BookOfShadowsGameConfig.instance.ACTIVE_ROWS === defaultRows && this._counter === 0 && BookOfShadowsGameConfig.instance.ACTIVE_ROWS_ALLOWED_JURISDICTION) {
            const tl: TimelineLite = new TimelineLite();

            tl.add(() => {
                this._view.playRowTutorial();
            }, startOffset);

            tl.add(() => {
                this._view.stopRowTutorial();
            }, (startOffset + (duration / 2)));

            tl.to(this, (startOffset + (duration / 2)), {}, 0);

            return tl;
        } else if (JSON.stringify(BookOfShadowsGameConfig.instance.LOCKED_REEL) === defaultLockSetUp && this._counter === 1 && BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_JURISDICTION) {
            const tl: TimelineLite = new TimelineLite();

            tl.add(() => {
                this._view.playLockTutorial();
                BookOfShadowsGameModuleConfig.reelLockController.playBounceForGameTutorial();
            }, startOffset);

            tl.add(() => {
                BookOfShadowsGameModuleConfig.reelLockController.stopBounceForGameTutorial();
                this._view.stopLockTutorial();
            }, (startOffset + (duration / 2)));

            tl.to(this, (startOffset + (duration / 2)), {}, 0);

            return tl;
        } else if (!this._hasFSFinished && this._counter % 7 === 0) {
            const tl: TimelineLite = new TimelineLite();

            if (BookOfShadowsGameConfig.instance.ACTIVE_ROWS === defaultRows) {
                tl.add(() => {
                    if (BookOfShadowsGameConfig.instance.ACTIVE_ROWS_ALLOWED_JURISDICTION) {
                        this._view.playRowTutorial();
                    }
                }, startOffset);

                tl.add(() => {
                    this._view.stopRowTutorial();

                    if (JSON.stringify(BookOfShadowsGameConfig.instance.LOCKED_REEL) === defaultLockSetUp && BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_JURISDICTION) {
                        this._view.playLockTutorial();
                        BookOfShadowsGameModuleConfig.reelLockController.playBounceForGameTutorial();

                        tl.add(() => {
                            BookOfShadowsGameModuleConfig.reelLockController.stopBounceForGameTutorial();
                            this._view.stopLockTutorial();
                        }, (startOffset + duration));

                        tl.to(this, (startOffset + (duration / 2)), {}, 0);
                    }
                }, (startOffset + (duration / 2)));

                tl.to(this, (startOffset + (duration / 2)), {}, 0);
            } else if (JSON.stringify(BookOfShadowsGameConfig.instance.LOCKED_REEL) === defaultLockSetUp && BookOfShadowsGameConfig.instance.LUCKY_LOCKS_ALLOWED_JURISDICTION) {
                tl.add(() => {
                    this._view.playLockTutorial();
                    BookOfShadowsGameModuleConfig.reelLockController.playBounceForGameTutorial();
                }, startOffset);

                tl.add(() => {
                    BookOfShadowsGameModuleConfig.reelLockController.stopBounceForGameTutorial();
                    this._view.stopLockTutorial();
                }, (startOffset + (duration / 2)));

                tl.to(this, (startOffset + (duration / 2)), {}, 0);
            }

            return tl;
        }
        this._hasFSFinished = false;

        return null;
    }

    /**
     * @inheritDoc
     */
    public abort(): TimelineLite | null {
        const tl: TimelineLite = new TimelineLite();

        tl.add(() => {
            this._view.stopRowTutorial();
            this._view.stopLockTutorial();

            BookOfShadowsGameModuleConfig.reelLockController.stopBounceForGameTutorial();
        });

        return tl;
    }
}